import { computed, ref, watch, watchEffect } from "vue";
import { useCookieStorage } from "@sazka/utils/use-storage";
import { usePageVisibility } from "@sazka/utils/composables/use-page-visibility";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { NgBalance, NgRestoreSession, NgSession } from "./model/ng-fw";
import { getNgTimeDuration } from "./ng-integration";
import { logLogin } from "@sazka/utils/log-login";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";

const storage = useCookieStorage();

const session = ref<NgSession | null>(null);
const balance = ref<NgBalance | null>(null);
const logonDate = ref<number | null>(null);
const showBalance = ref(storage.getItem("s-balance-visible") !== "false");
const isSessionStateFromCacheReady = ref(false);
const forceSessionRestore = !!(
    window?.player?.forceSessionRestore &&
    window?.player?.id &&
    window?.player?.sessionToken
);

/**
 * in hallers
 */
const balanceNumeric = computed(() => (balance.value?.Balance ? parseInt(balance.value.Balance) : null));
const isLoggedIn = computed(() => !!session.value?.SessionToken);

const { isDocumentVisible } = usePageVisibility();
const loginDurationMilliseconds = ref(0);
const refreshInterval = ref<number | null>(null);
const loginDurationFormatted = computed(() => DateTimeUtils.formatDuration(loginDurationMilliseconds.value));

export const useNgSession = () => ({
    session,
    balance,
    balanceNumeric,
    showBalance,
    isLoggedIn,
    logonDate,
    loginDurationMilliseconds,
    loginDurationFormatted,
    isSessionStateFromCacheReady,
    init,
    restoreFromWebView,
    refreshBalance,
    refreshSession,
    hasEnoughMoney,
    getMissingMoney,
    clearSession,
    showRegistrationOrLogin,
    restoreSessionFromApi,
});

function clearSession() {
    session.value = null;
}

function init() {
    if (forceSessionRestore) {
        session.value = {
            SessionToken: window.player.sessionToken,
            PlayerID: window.player.id,
            PlayerName: "",
        };
    } else {
        initFromCookies();
    }

    setupWatchers();
}

async function restoreFromWebView() {
    if (!forceSessionRestore) return;

    if (isLoggedIn.value) {
        // logout previously logged in player and restore new session
        if (session.value.PlayerID === window.player.id) {
            isSessionStateFromCacheReady.value = true;
            return;
        }
        await logout();
    }

    await restoreSessionPassedFromBackend();
    isSessionStateFromCacheReady.value = true;
}

function restoreSessionFromApi(session: NgRestoreSession) {
    return new Promise<void>((res) => {
        watch(
            isLoggedIn,
            (newVal) => {
                if (newVal) {
                    res();
                }
            },
            { immediate: true }
        );
        ng_fw.restoreSession({
            isPostLoginPopupRequired: false,
            ...session,
        });
        logLogin();
    });
}

function restoreSessionPassedFromBackend() {
    return new Promise<void>((res) => {
        const { id, sessionToken } = window.player;
        let notAbleToRestoreSessionTimeout = 0;

        // watch for login
        const stopWatch = watch(isLoggedIn, (isLoggedIn) => {
            if (!isLoggedIn) return;

            clearTimeout(notAbleToRestoreSessionTimeout);
            stopWatch();
            logLogin();
            res();
        });

        ng_fw.restoreSession({
            isPostLoginPopupRequired: false,
            playerId: id,
            sessionToken,
        });

        notAbleToRestoreSessionTimeout = window.setTimeout(() => {
            showAlert({
                title: "Přihlášení se nezdařilo",
                subText: "Nepodařilo se přenést přihlášení z mobilní aplikace",
            });
            stopWatch();
            res();
        }, 10_000);
    });
}

function initFromCookies() {
    const sessionToken = storage.getItem("SessionToken");
    if (sessionToken) {
        balance.value = {
            DisplayBalance: storage.getItem("s-balance"),
        };
        logonDate.value = parseInt(storage.getItem("s-logon-date")) || Date.now();
        session.value = {
            SessionToken: sessionToken,
            PlayerID: storage.getItem("PlayerID"),
            PlayerName: storage.getItem("s-player-name"),
        };
    }
    isSessionStateFromCacheReady.value = true;
}

async function refreshBalance(force = false) {
    if (force) await ng_fw.refreshBalance();
    const newBalance = ng_fw.getDisplayBalance();
    balance.value = newBalance;
}

function refreshSession() {
    const newSession = ng_fw.getSessionDetails();
    session.value = newSession;
}

/**
 * Checks wether user has money for bet
 * @param requiredAmount value in Kc
 */
function hasEnoughMoney(requiredAmount: number) {
    return (balanceNumeric.value || 0) >= requiredAmount * 100;
}

/**
 * Gets missing money for bet
 * @param requiredAmount value in Kc
 */
function getMissingMoney(requiredAmount: number) {
    const sub = requiredAmount * 100 - balanceNumeric.value;
    return sub < 0 ? 0 : sub;
}

function setCookie(name: string, value: string, options?: Cookies.CookieAttributes) {
    if (value == null) {
        storage.removeItem(name);
    } else {
        storage.setItem(name, value, "essential", options);
    }
}

const startLoginDurationTimer = () => {
    loginDurationMilliseconds.value = logonDate.value ? Date.now() - logonDate.value : 0;
    refreshInterval.value = window.setInterval(() => {
        loginDurationMilliseconds.value += 1000;
    }, 1000);
};

function setupWatchers() {
    watchEffect(() => setCookie("s-balance-visible", showBalance.value as any, { expires: 365 }));
    watchEffect(() => setCookie("s-balance", balance.value?.DisplayBalance));
    watchEffect(() => setCookie("s-logon-date", logonDate.value as any));
    watchEffect(() => {
        setCookie("PlayerID", session.value?.PlayerID, { sameSite: "strict" });
        setCookie("SessionToken", session.value?.SessionToken, { sameSite: "strict" });
        setCookie("s-player-name", session.value?.PlayerName);
        if (!session.value?.SessionToken) {
            balance.value = null;
            logonDate.value = null;
        } else if (session.value?.LoginTimeStamp) {
            logonDate.value = getNgTimeDuration(session.value.LoginTimeStamp, session.value.ServerTimeStamp);
        }
        setAuthenticatedBodyClass();
    });

    watch(
        [logonDate, isDocumentVisible],
        () => {
            clearInterval(refreshInterval.value);
            if (isLoggedIn.value) startLoginDurationTimer();
        },
        {
            immediate: true,
        }
    );
}

function setAuthenticatedBodyClass() {
    if (isLoggedIn.value) {
        document.body.classList.add("authenticated");
        document.body.classList.remove("unauthenticated");
    } else {
        document.body.classList.remove("authenticated");
        document.body.classList.add("unauthenticated");
    }
}

async function showRegistrationOrLogin() {
    ng_fw.showLogin();
}

function logout() {
    return new Promise<void>((res) => {
        if (!isLoggedIn.value) {
            res();
            return;
        }
        const stopWatch = watch(isLoggedIn, (newVal) => {
            if (!newVal) {
                stopWatch();
                res();
            }
        });
        ng_fw.logout();
    });
}
